import {useState} from "react";
import {Alert, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Radio, RadioGroup} from "@mui/material";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../misc/SmoothActionBar";
import {api} from "../../../../../api/API";
import {ElevationApp} from "../ElevationApp";

// the save prompt in edit-global mode: re-render the openings that link the global door elevation,
// or only update its drawing.
export function GlobalDoorSaveDialog(props: {
    app: ElevationApp;
    ddl: number;
    name: string;
    onCancel(): void;
    onSaved(): void;
}) {
    const [mode, setMode] = useState<"rerender" | "data">("rerender");
    const links = useAsync2((ddl: number) => api.elevationTool.globalDoorLinks({ddl}), props.ddl, [props.ddl]);

    const list = links.result ?? [];
    const projects = new Set(list.map(l => l.project)).size;
    const reached = list.filter(l => l.rerendered);
    const reachedProjects = new Set(reached.map(l => l.project)).size;

    const save = useAsyncAction(async (rerender: boolean) => {
        await props.app.saveGlobalDoor({rerender});
        props.onSaved();
    }, [props.app, props.onSaved]);

    return (
        <Dialog2 open onClose={props.onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Save global door elevation "{props.name}"</DialogTitle>
            <DialogContent>
                {links.LoadingOrErrorElement}
                {links.result && (
                    <DialogContentText>
                        {list.length} opening{list.length === 1 ? "" : "s"} in {projects} project{projects === 1 ? "" : "s"} link this elevation.
                    </DialogContentText>
                )}

                <RadioGroup value={mode} onChange={e => setMode(e.target.value as "rerender" | "data")} sx={{mt: 1}}>
                    <FormControlLabel
                        value="rerender"
                        control={<Radio size="small" />}
                        label={`Re-render linked openings (${reached.length} in ${reachedProjects} project${reachedProjects === 1 ? "" : "s"})`}
                    />
                    <FormControlLabel
                        value="data"
                        control={<Radio size="small" />}
                        label="Update the drawing only — do not re-render"
                    />
                </RadioGroup>

                <Alert severity="warning" sx={{mt: 2}}>
                    No changeset records this change to the drawing, also for projects in production.
                    {mode === "rerender" && <> The re-render updates lite kits, products and renderings of
                        in-progress and quote projects. In an in-progress project, these updates can open a
                        changeset. Other projects keep their renderings until they render again.</>}
                    {mode === "data" && <> Linked openings keep their renderings until they render again.</>}
                </Alert>
            </DialogContent>

            <SmoothActionBar
                loading={save.loading}
                error={save.error}
                onCancel={props.onCancel}
                onSubmit={() => save.callback(mode === "rerender")}
                actionName="Save"
                submittingWord="Saving…"
            />
        </Dialog2>
    );
}
